/**
 * app-top-nav.tsx — Top-of-page navigation bar matching the
 * Crextio-style wireframe in
 * [docs/wireframes/iltizam2/app.jsx](../../docs/wireframes/iltizam2/app.jsx).
 *
 * Replaces the previous left-side sidebar entirely. The same
 * role-aware `buildSections()` helper (kept in `app-sidebar.tsx`)
 * is reused to drive the items — we just present them as pills in
 * a single horizontal capsule instead of as a vertical list.
 *
 * Layout (RTL):
 *
 *   ┌─[Logo]───[ pill | pill | pill | ... ]──────[⚙][🔔][AvatarChip]─┐
 *
 * The logo is the wireframe's mark: a dark rounded square with an
 * inset yellow square. The pill capsule centres in the row and
 * wraps when the available width can't hold every item. The right
 * cluster carries the settings cog, notifications bell with unread
 * badge, the language toggle, and a yellow avatar disc bearing the
 * first letter of the user's first name.
 */

import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, LogOut, Settings } from 'lucide-react';
import { buildSections, type NavItem } from '@/components/app-sidebar';
import { LanguageToggle } from '@/components/language-toggle';
import { useAuthStore } from '@/lib/auth-store';
import { authApi } from '@/lib/api/auth';
import { cn } from '@/lib/utils';

export function AppTopNav(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshToken, clear } = useAuthStore();

  // Reuse the role-aware section catalogue from the old sidebar
  // helper and flatten it. Order is preserved (Dashboard first,
  // Admin section last, etc.). Items still get filtered by role
  // because `buildSections` already gates each push.
  const items: NavItem[] = buildSections(t, user).flatMap((s) => s.items);

  const isActive = (to: string): boolean => {
    if (to === '/app') return location.pathname === '/app';
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  const handleLogout = async (): Promise<void> => {
    if (refreshToken) {
      try {
        await authApi.logout(refreshToken);
      } catch {
        /* intentionally swallowed — local clear is the source of truth */
      }
    }
    clear();
    navigate('/auth/login', { replace: true });
  };

  const initial = user?.firstName?.[0] ?? 'م';

  return (
    <div className="mb-6 flex flex-nowrap items-center justify-between gap-3">
      {/* Logo */}
      <NavLink to="/app" className="flex shrink-0 items-center gap-2.5">
        <span
          aria-hidden="true"
          className="flex h-9 w-9 items-center justify-center rounded-xl bg-iltizam-ink"
        >
          <span className="h-4 w-4 rounded-[5px] bg-iltizam-yellow" />
        </span>
        <span className="text-[21px] font-extrabold tracking-[0.5px] text-iltizam-ink">
          {t('brand.name')}
        </span>
      </NavLink>

      {/* Pill capsule — single row regardless of role or language.
          Horizontally scrollable when content exceeds the available
          width, with the scrollbar hidden so the capsule edges stay
          crisp. min-w-0 lets the flex item actually shrink. */}
      <nav
        aria-label={t('nav.dashboard')}
        className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto whitespace-nowrap rounded-pill border border-iltizam-line bg-white p-1.5 shadow-soft [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        {items.map((item) => {
          const active = isActive(item.to);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/app'}
              className={cn(
                'inline-flex shrink-0 items-center gap-2 rounded-pill px-4 py-2 text-[12.5px] font-bold transition-colors',
                active
                  ? 'bg-iltizam-ink text-white'
                  : 'text-iltizam-muted hover:bg-iltizam-warm hover:text-iltizam-ink',
              )}
            >
              <item.icon className="h-4 w-4" aria-hidden="true" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Right cluster */}
      <div className="flex shrink-0 items-center gap-2">
        {/* Settings gear is visible to every role on every page. Lands
            on /app/settings/profile — the Profile tab is the universal
            entry; role-restricted tabs (CM / Admin) are conditionally
            rendered inside SettingsPage so a Risk Manager or Executive
            Viewer simply sees the Profile-only view rather than a 403. */}
        <button
          type="button"
          aria-label="settings"
          onClick={() => navigate('/app/settings/profile')}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-iltizam-line bg-white text-iltizam-ink transition-colors hover:bg-iltizam-warm"
        >
          <Settings className="h-[18px] w-[18px]" aria-hidden="true" />
        </button>
        <NotificationsButton />
        <LanguageToggle />
        <button
          type="button"
          onClick={handleLogout}
          aria-label={t('common.logout')}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-iltizam-line bg-white text-iltizam-ink transition-colors hover:bg-iltizam-red-soft hover:text-[#9A2A22]"
        >
          <LogOut className="h-[18px] w-[18px]" aria-hidden="true" />
        </button>
        {user ? (
          <span
            aria-label={`${user.firstName} ${user.lastName}`}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-iltizam-yellow text-[15px] font-extrabold text-iltizam-ink"
          >
            {initial}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Notifications bell, restyled to match the wireframe's icon
 * button vocabulary. The existing NotificationsBell component is
 * intentionally not reused — its visual treatment was the
 * slate/blue header style we're abandoning.
 */
function NotificationsButton(): JSX.Element {
  const navigate = useNavigate();
  // Bypass the in-place dropdown for now; route into the
  // home dashboard's escalations card, which is where the
  // wireframe surfaces actionable inbox items. The dedicated
  // notification surface gets its own redesign in Phase 2.
  return (
    <button
      type="button"
      aria-label="notifications"
      onClick={() => navigate('/app')}
      className="relative flex h-10 w-10 items-center justify-center rounded-full border border-iltizam-line bg-white text-iltizam-ink transition-colors hover:bg-iltizam-warm"
    >
      <Bell className="h-[18px] w-[18px]" aria-hidden="true" />
    </button>
  );
}
